import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react'
import { useRouteContext } from '@tanstack/react-router'
import { Tenant } from '@/types'
import { TenantService } from './tenant-service'
import { configureAmplifyWithTenant } from '@/lib/amplify'
import { useAmplify } from '@/contexts/amplify-context'
import type { TenantRouterContext } from '@/lib/router/tenant-detection'
import { undoManager } from '@/services/undoManager'

interface TenantContextType {
  tenant: Tenant | null
  hostname: string | null
  tenantDomain: string | null
  isLoading: boolean
  error: string | null
  refreshTenant: () => Promise<void>
  switchTheme: (mode: 'light' | 'dark') => void
  currentThemeMode: 'light' | 'dark'
  isThemeLoaded: boolean
}

const TenantContext = createContext<TenantContextType | undefined>(undefined)

// Note: Service initialization moved to AuthProvider to prevent premature API calls
// This ensures services requiring authentication only initialize AFTER user auth status is determined

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [hostname, setHostname] = useState<string | null>(null)
  const [tenantDomain, setTenantDomain] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentThemeMode, setCurrentThemeMode] = useState<'light' | 'dark'>(
    'light'
  )
  const [isThemeLoaded, setIsThemeLoaded] = useState(false)

  const tenantService = useMemo(() => new TenantService(), [])
  const { setConfigured, setError: setAmplifyError } = useAmplify()

  // Get subdomain and tenantDomain from router context (provided by __root.tsx beforeLoad)
  const routerContext = useRouteContext({ from: '__root__' }) as TenantRouterContext | undefined

  const loadTenant = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      setIsThemeLoaded(false)

      // Get subdomain and tenantDomain from router context
      const subdomain = routerContext?.subdomain || 'asdasd'
      const effectiveTenantDomain = routerContext?.tenantDomain || window.location.hostname

      // Get tenant from hostname (using router-provided tenantDomain)
      const currentHostname = window.location.hostname
      const tenantData = await tenantService.getTenantByHostname(
        effectiveTenantDomain
      )
      setTenant(tenantData)
      setHostname(currentHostname)

      // Set the effective tenant domain for API calls (from router context)
      setTenantDomain(effectiveTenantDomain)

      // Configure Amplify with tenant-specific settings
      configureAmplifyWithTenant(tenantData)
      setConfigured(true) // Notify AmplifyProvider that configuration is complete

      // Initialize UndoManager with tenant domain
      if (effectiveTenantDomain) {
        undoManager.initialize(effectiveTenantDomain)
      }

      // NOTE: Service initialization moved to AuthProvider to prevent premature API calls
      // Services requiring authentication should only initialize AFTER user auth status is determined

      // Set initial theme mode (check saved preference first)
      const savedTheme = localStorage.getItem('theme-mode') as
        | 'light'
        | 'dark'
        | null
      const initialMode =
        savedTheme && tenantData.features?.darkMode ? savedTheme : 'light'
      setCurrentThemeMode(initialMode)
      setIsThemeLoaded(true) // Mark theme as loaded so children can render
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to load tenant configuration'
      setError(errorMessage)
      setAmplifyError(errorMessage) // Also notify AmplifyProvider about the error
      console.error('Tenant loading error:', err)

      // No fallback available, just log the error
      setAmplifyError(
        'Failed to configure authentication. Please try refreshing the page.'
      )
    } finally {
      setIsLoading(false)
    }
  }, [tenantService, setConfigured, setAmplifyError, routerContext])

  useEffect(() => {
    loadTenant()
  }, [loadTenant])

  useEffect(() => {
    // Check for saved theme preference on load
    const savedTheme = localStorage.getItem('theme-mode') as
      | 'light'
      | 'dark'
      | null
    if (savedTheme && tenant?.features?.darkMode) {
      setCurrentThemeMode(savedTheme)
    }
  }, [tenant?.features?.darkMode])

  const refreshTenant = async () => {
    await loadTenant()
  }

  const switchTheme = (mode: 'light' | 'dark') => {
    if (!tenant?.features?.darkMode && mode === 'dark') {
      console.warn('Dark mode is not enabled for this tenant')
      return
    }

    setCurrentThemeMode(mode)

    // Store user preference
    localStorage.setItem('theme-mode', mode)
  }

  const value = {
    tenant,
    hostname,
    tenantDomain,
    isLoading,
    error,
    refreshTenant,
    switchTheme,
    currentThemeMode,
    isThemeLoaded,
  }

  // Don't render children until tenant is loaded and theme is applied
  if (isLoading || !isThemeLoaded) {
    return (
      <div className='flex items-center justify-center min-h-screen bg-white'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4'></div>
          <p className='text-secondary'></p>
        </div>
      </div>
    )
  }

  // Show error state if tenant failed to load
  if (error && !tenant) {
    return (
      <div className='flex items-center justify-center min-h-screen bg-red-50'>
        <div className='text-center p-8 bg-primary rounded-lg shadow-lg max-w-md'>
          <div className='text-red-600 mb-4'>
            <svg
              className='w-16 h-16 mx-auto'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          </div>
          <h2 className='text-xl font-semibold text-primary mb-2'>
            Tenant Configuration Error
          </h2>
          <p className='text-secondary mb-4'>{error}</p>
          <button
            onClick={refreshTenant}
            className='bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors'
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  )
}

export function useTenant() {
  const context = useContext(TenantContext)
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}
